// :cow: Cosmose CONFIDENTIAL :iso:
import { AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges, ViewChild } from '@angular/core';
import * as Chart from 'chart.js';
import { SpyLineChartTooltipPositionService } from '@coa/app/shared/spy-line-chart-tooltip-position-service/spy-line-chart-tooltip-position.service';
import { ScaleTicks } from '@coa/app/shared/chart-js/chartjs-tooltip';
import { getBaseVisitsSpyLineChart } from '@coa/app/shared/chart-js/base-visits-spy-line-chart';
import { getPrimaryDataset, getSecondaryDataset } from '@coa/app/shared/chart-js/primary-dataset';
import { Tooltip } from '@coa/app/shared/unique-visits-chart/model/tooltip';
import { DataSeries } from '@coa/app/shared/spy-line-chart/model/data-series';

@Component({
  selector: 'coa-multiple-series-chart',
  templateUrl: './multiple-series-chart.component.html',
  styleUrls: ['./multiple-series-chart.component.scss'],
})
export class MultipleSeriesChartComponent implements AfterViewInit, OnChanges {
  @Input() clientId: string;
  @Input() hexColor: string;
  @Input() label: string;
  @Input() yAxisTicksConfig: ScaleTicks;
  @Input() primaryDataset: DataSeries;
  @Input() secondaryDataset: DataSeries;
  @Input() customColors = false;

  @ViewChild('paymentsChart', {read: ElementRef}) chartRef: ElementRef<HTMLCanvasElement>;
  @ViewChild('tooltip', {read: ElementRef}) tooltip: ElementRef<HTMLDivElement>;

  chart: Chart;
  currentTooltip: Tooltip;

  constructor(
    private cdr: ChangeDetectorRef,
    private tooltipPositionService: SpyLineChartTooltipPositionService,
  ) {
  }

  ngAfterViewInit(): void {
    this.drawChart();
  }

  ngOnChanges(): void {
    this.drawChart();
  }

  private drawChart(): void {
    if (!this.chartRef) {
      return;
    }
    if (!this.chart) {
      this.chart = new Chart(this.chartRef.nativeElement.getContext('2d'), getBaseVisitsSpyLineChart());
    }
    if (!this.primaryDataset) {
      return;
    }
    this.chart.data.labels = this.primaryDataset.chartSeries.map((element) => element.label);
    this.chart.data.datasets = [getPrimaryDataset(this.primaryDataset.label, this.primaryDataset.chartSeries.map((element) => element.value))];
    this.chart.options.tooltips.custom = ((tooltip: Chart.ChartTooltipModel): void => this.tooltipCallback(tooltip));
    if (this.secondaryDataset) {
      this.chart.data.datasets.push(getSecondaryDataset(this.secondaryDataset.label, this.secondaryDataset.chartSeries.map((element) => element.value)));
    }
    if (this.customColors) {
      if (this.chart.data.datasets[0]) {
        this.chart.data.datasets[0] = getCustomPrimaryDataset(this.primaryDataset.label, this.primaryDataset.chartSeries.map((element) => element.value));
      }
      if (this.chart.data.datasets[1]) {
        this.chart.data.datasets[1] = getCustomSecondaryDataset(this.secondaryDataset.label, this.secondaryDataset.chartSeries.map((element) => element.value));
      }
    }
    this.chart.update();
  }

  private tooltipCallback(tooltip: Chart.ChartTooltipModel): void {
    if (!tooltip.body) {
      this.currentTooltip = null;
      return;
    }
    this.currentTooltip = {
      last: (tooltip.dataPoints[0].index === (this.primaryDataset.chartSeries.length - 1)),
      date: this.primaryDataset.chartSeries[tooltip.dataPoints[0].index].label,
      title: this.label,
      primaryValue: this.primaryDataset.chartSeries[tooltip.dataPoints[0].index].value,
      secondaryValue: this.secondaryDataset ? this.secondaryDataset.chartSeries[tooltip.dataPoints[0].index]?.value : undefined,
      color: this.hexColor,
      primaryLabel: this.primaryDataset.label,
      secondaryLabel: this.secondaryDataset ? this.secondaryDataset.label : undefined,
    };
    this.cdr.detectChanges();
    const position = this.tooltipPositionService.repositionTooltip(
      this.chart.canvas.getBoundingClientRect().width,
      this.tooltip.nativeElement.offsetWidth,
      tooltip.caretX,
      tooltip.caretY,
    );
    this.tooltip.nativeElement.style.setProperty('left', `${ position.left }px`);
    this.tooltip.nativeElement.style.setProperty('top', `${ position.top }px`);
  }
}

export function getCustomPrimaryDataset(label: string, values: number[]) {
  return {
    label,
    borderColor: '#00a2ff',
    borderWidth: 2,
    data: values,
    pointRadius: 0,
    pointHoverRadius: 5,
    fill: true,
    pointBackgroundColor: '#00a2ff',
    backgroundColor: 'transparent',
  };
}

export function getCustomSecondaryDataset(label: string, values: number[]) {
  return {
    label: label,
    borderColor: '#9C60FF',
    borderWidth: 2,
    data: values,
    pointRadius: 0,
    pointHoverRadius: 5,
    fill: true,
    pointBackgroundColor: '#9C60FF',
    backgroundColor: 'transparent',
  };
}
